import { parseSearchFilters } from "@/features/utils/search-filter-parser";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/features/utils/i18n";

interface HighlightedSearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

/**
 * Component that highlights filter syntax in search input
 * Highlights patterns like: column=value, column>123, column~text, etc.
 */
export function HighlightedSearchInput({
  value,
  onChange,
  placeholder,
  className,
}: HighlightedSearchInputProps) {
  const { t } = useTranslation();
  placeholder ??= t("common.select.search");
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  const renderHighlightedText = (text: string) => {
    if (!text) return null;

    // Parse the search to identify filter patterns
    const parsed = parseSearchFilters(text);
    const hasFilters = Object.keys(parsed.filters).length > 0;

    if (!hasFilters) {
      // No filters, just return plain text
      return <span className="text-black dark:text-white">{text}</span>;
    }

    // Find all filter patterns in the original text
    const filterPatterns = [
      // Order matters - more specific patterns first
      {
        regex: /(\w+)\s*=\s*["']([^"']*?)["']/g,
        className:
          "text-blue-600 font-medium bg-slate-100 dark:bg-slate-900/30 ",
      },
      {
        regex: /(\w+)\s*>=\s*([^\s]+)/g,
        className:
          "text-green-600 font-medium bg-green-100 dark:bg-green-900/30 ",
      },
      {
        regex: /(\w+)\s*<=\s*([^\s]+)/g,
        className:
          "text-green-600 font-medium bg-green-100 dark:bg-green-900/30 ",
      },
      {
        regex: /(\w+)\s*!=\s*([^\s]+)/g,
        className: "text-red-600 font-medium bg-red-100 dark:bg-red-900/30 ",
      },
      {
        regex: /(\w+)\s*>\s*([^\s]+)/g,
        className:
          "text-green-600 font-medium bg-green-100 dark:bg-green-900/30 ",
      },
      {
        regex: /(\w+)\s*<\s*([^\s]+)/g,
        className:
          "text-green-600 font-medium bg-green-100 dark:bg-green-900/30 ",
      },
      {
        regex: /(\w+)\s*~\s*([^\s]+)/g,
        className:
          "text-purple-600 font-medium bg-purple-100 dark:bg-purple-900/30 ",
      },
      {
        regex: /(\w+)\s*=\s*([^\s]+)/g,
        className:
          "text-blue-600 font-medium bg-slate-100 dark:bg-slate-900/30 ",
      },
    ];

    let parts: Array<{
      text: string;
      highlighted: boolean;
      className?: string;
    }> = [{ text, highlighted: false }];

    // Apply highlighting for each pattern
    for (const pattern of filterPatterns) {
      const newParts: typeof parts = [];

      for (const part of parts) {
        if (part.highlighted) {
          newParts.push(part);
          continue;
        }

        const regex = new RegExp(pattern.regex.source, pattern.regex.flags);
        const matches = Array.from(part.text.matchAll(regex));

        if (matches.length === 0) {
          newParts.push(part);
          continue;
        }

        let lastIndex = 0;
        for (const match of matches) {
          const matchStart = match.index!;
          const matchEnd = matchStart + match[0].length;

          // Add text before match
          if (matchStart > lastIndex) {
            newParts.push({
              text: part.text.slice(lastIndex, matchStart),
              highlighted: false,
            });
          }

          // Add highlighted match
          newParts.push({
            text: match[0],
            highlighted: true,
            className: pattern.className,
          });

          lastIndex = matchEnd;
        }

        // Add remaining text after last match
        if (lastIndex < part.text.length) {
          newParts.push({
            text: part.text.slice(lastIndex),
            highlighted: false,
          });
        }
      }

      parts = newParts;
    }

    return (
      <span>
        {parts.map((part, index) => (
          <span
            key={index}
            className={
              part.highlighted
                ? cn("rounded-full px-[3px] -mx-[3px]", part.className)
                : "text-black dark:text-white"
            }
          >
            {part.text}
          </span>
        ))}
      </span>
    );
  };

  return (
    <div className="relative">
      {/* Invisible input for actual functionality */}
      <input
        type="text"
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        className={cn(
          "w-full bg-transparent border-0 outline-0 text-transparent relative z-10 px-3 py-2 text-sm whitespace-nowrap overflow-hidden caret-black dark:caret-white",
          className,
        )}
      />

      {/* Highlighted overlay */}
      <div
        className={cn(
          "absolute inset-0 pointer-events-none flex items-center px-3 py-2 text-sm",
          "whitespace-nowrap overflow-hidden",
        )}
      >
        {value ? (
          renderHighlightedText(value)
        ) : (
          <span className="text-muted-foreground">{placeholder}</span>
        )}
      </div>
    </div>
  );
}
